import { useEffect } from "react";
import { useForm } from "react-hook-form";

import useHaulierAccounts from "../../data/useHaulierAccounts";
import useWasteTracking from "../../data/useWasteTracking";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { WasteReceiptCarrierType } from "../../models/wasteTracking/IWasteReceiptRow";
import { haulierOptions } from "../../utils/hauliers";
import { buildBulkPatch, carrierTypeLabels, emptyBulkFormValues, IWasteReceiptBulkFormValues } from "../../utils/wasteTracking";
import useNotification from "../notifications/useNotifications";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { CheckboxInput } from "../shared/Inputs/CheckboxInput";
import { DateTimeInput } from "../shared/Inputs/DateTimeInput";
import { NumberInput } from "../shared/Inputs/NumberInput";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";

interface WasteReceiptBulkUpdateModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  vehicleIds: string[];
  onUpdated?: () => void;
}

const carrierTypeOptions = [
  { id: "", name: "Leave unchanged" },
  { id: String(WasteReceiptCarrierType.OwnCarrier), name: carrierTypeLabels[WasteReceiptCarrierType.OwnCarrier] },
  { id: String(WasteReceiptCarrierType.HouseholdDropOff), name: carrierTypeLabels[WasteReceiptCarrierType.HouseholdDropOff] },
  { id: String(WasteReceiptCarrierType.ThirdPartyCarrier), name: carrierTypeLabels[WasteReceiptCarrierType.ThirdPartyCarrier] },
];

const estimateOptions = [
  { id: "", name: "Leave unchanged" },
  { id: "true", name: "Estimated" },
  { id: "false", name: "Weighed (actual)" },
];

// Blank means "leave unchanged" - only the fields that are filled in are applied to every
// selected row, so a run of ten own-driver collections can be stamped in one go.
export default function WasteReceiptBulkUpdateModal({ open, setOpen, vehicleIds, onUpdated }: WasteReceiptBulkUpdateModalProps) {
  const { bulkUpdate } = useWasteTracking("WasteReceiptBulkUpdateModal");
  const { haulierAccounts } = useHaulierAccounts("WasteReceiptBulkUpdateModal");
  const { saveHandlers } = useDefaultCRUDHandlers("Waste receipts");
  const { addNotification } = useNotification();
  const { register, handleSubmit, reset, setValue, watch } = useForm<IWasteReceiptBulkFormValues>({ defaultValues: emptyBulkFormValues });

  const useCollectedDate = watch("useCollectedDate");
  const haulierSelectOptions = [
    { id: "", name: "Leave unchanged" },
    ...haulierOptions(haulierAccounts.data?.data).map((o) => ({ id: o.value, name: o.label })),
  ];

  useEffect(() => {
    if (open) reset(emptyBulkFormValues);
  }, [open, reset]);

  const onSubmit = (values: IWasteReceiptBulkFormValues) => {
    const patch = buildBulkPatch(values);

    if (!patch) {
      addNotification({ variant: "warn", primaryText: "Fill in at least one field to apply" });
      return;
    }

    bulkUpdate.mutate(
      { vehicleIds, patch },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          onUpdated?.();
        },
        onError: saveHandlers.onError,
      }
    );
  };

  return (
    <Modal width="max-w-3xl" open={open} setOpen={setOpen}>
      <h1 className="text-xl">Bulk update waste receipts</h1>
      <p className="text-sm text-gray-500">
        Applies to {vehicleIds.length} selected vehicle{vehicleIds.length === 1 ? "" : "s"}. Anything left blank is not changed.
      </p>

      <form className="mt-4 space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2 flex items-end gap-6">
            <CheckboxInput label="Set date received to each vehicle's collected date" register={register} registerName="useCollectedDate" />
          </div>
          <DateTimeInput label="Date received" register={register} registerName="dateTimeReceived" disabled={!!useCollectedDate} />
          <NumberInput label="Weight (kg)" register={register} registerName="weightKg" step="1" min={1} />
          <SelectInput label="Weight type" register={register} registerName="weightIsEstimate" options={estimateOptions} setValue={setValue} watch={watch} />
          <TextInput label="Consignment note code" register={register} registerName="hazardousWasteConsignmentCode" placeholder="e.g. CJTILE/A0001" />
          <SelectInput label="Carrier type" register={register} registerName="carrierType" options={carrierTypeOptions} setValue={setValue} watch={watch} />
          <div>
            <SelectInput label="Haulier" register={register} registerName="haulierAccountId" options={haulierSelectOptions} setValue={setValue} watch={watch} cols="" />
            <p className="mt-1 text-xs text-gray-500">Picking a haulier makes every selected row third-party and takes the carrier name and registration from it.</p>
          </div>
          <TextInput label="Carrier registration number" register={register} registerName="carrierRegistrationNumber" placeholder="e.g. CBDU123456" />
          <TextInput label="Carrier name" register={register} registerName="carrierOrganisationName" />
          <TextInput label="Carrier vehicle registration" register={register} registerName="carrierVehicleRegistration" placeholder="Truck / trailer reg" />
          <TextInput label="Recovery code" register={register} registerName="recoveryCode" placeholder="e.g. R13" />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <ButtonInput label="Cancel" isSubmit={false} onClick={() => setOpen(false)} classes="bg-gray-500 hover:bg-gray-600" />
          <ButtonInput label="Apply to selected" isSubmit={true} onClick={undefined} classes={undefined} isLoading={bulkUpdate.isLoading} />
        </div>
      </form>
    </Modal>
  );
}
